import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { workOrdersApi } from '../../api/workOrdersApi';
import { useSignalR } from '../../hooks/useSignalR';
import { useAuthStore } from '../../store/authStore';
import { ChatPanel } from '../../components/technician/ChatPanel';
import { AlertTriangle, CheckCircle2, Clock3, MessageSquare, Search, Wrench } from 'lucide-react';

interface WorkOrderBoardItem {
  workOrderId: number;
  workOrderNumber: string;
  vehicleYear?: number;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleColor?: string;
  vin?: string;
  licensePlate?: string;
  mileage?: number;
  customerFirstName?: string;
  customerLastName?: string;
  status: string;
  priority: string;
  description?: string;
  notes?: string;
  customerConcerns?: string;
  assignedTechName?: string;
  assignedTechInitials?: string;
  createdDate: string;
  completedDate?: string;
  vehicleInfo?: string; vehiclePlate?: string; customerName?: string; serviceDescription?: string;
}

export const MyJobsPage: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<WorkOrderBoardItem[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'all' | 'completed'>('active');
  const [newAssignmentBanner, setNewAssignmentBanner] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [chatJobId, setChatJobId] = useState<number | null>(null);
  const [unreadChatJobs, setUnreadChatJobs] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState('');
  const staffId = useAuthStore((s) => s.staffId);

  const { on, off } = useSignalR();

  // Load jobs on mount
  useEffect(() => {
    loadJobs();
  }, []);

  // Listen for new assignments
  useEffect(() => {
    const handleTechnicianAssigned = (...args: any[]) => {
      const workOrderId = args[0];
      const jobDetails = args[1];
      setNewAssignmentBanner({
        workOrderId,
        ...jobDetails
      });

      // Reload jobs to update the list
      loadJobs();

      // Auto-hide banner after 5 seconds
      setTimeout(() => setNewAssignmentBanner(null), 5000);
    };

    on('TechnicianAssigned', handleTechnicianAssigned);
    return () => {
      off('TechnicianAssigned', handleTechnicianAssigned);
    };
  }, [on, off]);

  // Listen for incoming chat messages — show badge on job card
  useEffect(() => {
    const handleNewChat = (...args: unknown[]) => {
      const woId = args[0] as number;
      const msg = args[1] as { senderStaffId: number };
      if (msg.senderStaffId === staffId) return;
      if (chatJobId === woId) return; // already viewing that chat
      setUnreadChatJobs((prev) => new Set([...prev, woId]));
    };
    on('NewChatMessage', handleNewChat);
    return () => off('NewChatMessage', handleNewChat);
  }, [on, off, staffId, chatJobId]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const data = await workOrdersApi.getWorkOrderBoard(true);
      setJobs(data);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      setLoadError('Jobs could not be loaded. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'inprogress':
        return '#7f9bd2'; // tertiary-container
      case 'scheduled':
      case 'pending':
      case 'booked':
        return '#bcc7de'; // secondary-fixed-dim
      case 'paused':
      case 'onhold':
      case 'awaitingparts':
        return '#f97316'; // primary-container
      case 'completed':
      case 'ready':
        return '#10b981'; // status-success
      default:
        return '#8c7164'; // outline
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'inprogress':
        return 'In Progress';
      case 'pending':
      case 'booked':
        return 'Scheduled';
      case 'awaitingparts':
        return 'Awaiting Parts';
      case 'completed':
        return 'Completed';
      case 'paused':
      case 'onhold':
        return 'Paused';
      default:
        return status || 'Unknown';
    }
  };

  const getActionButton = (job: WorkOrderBoardItem) => {
    const statusLower = job.status?.toLowerCase();
    if (statusLower === 'inprogress') {
      return { label: 'Open Job', color: '#9d4300' }; // primary
    } else if (statusLower === 'paused' || statusLower === 'onhold') {
      return { label: 'Resume Job', color: '#f97316' }; // primary-container
    } else if (statusLower === 'completed') {
      return { label: 'View Complete', color: '#10b981' }; // status-success
    } else {
      return { label: 'Start Job', color: '#f97316' }; // primary-container
    }
  };

  const filterJobs = (jobsToFilter: WorkOrderBoardItem[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (activeTab) {
      case 'active':
        return jobsToFilter.filter(j => {
          const status = j.status?.toLowerCase();
          return status === 'inprogress' || status === 'paused' || status === 'onhold' || status === 'pending' || status === 'booked';
        });
      case 'completed':
        return jobsToFilter.filter(j => {
          const status = j.status?.toLowerCase();
          if (status !== 'completed') return false;
          if (!j.completedDate) return false;
          const completedDate = new Date(j.completedDate);
          const completedToday = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate());
          return completedToday.getTime() === today.getTime();
        });
      default: // all
        return jobsToFilter;
    }
  };

  const displayJobs = filterJobs(jobs).filter((job) => {
    const haystack = `${job.workOrderNumber} ${job.vehicleYear} ${job.vehicleMake} ${job.vehicleModel} ${job.licensePlate} ${job.customerFirstName} ${job.customerLastName}`.toLowerCase();
    return haystack.includes(search.toLowerCase().trim());
  });
  const activeCount = jobs.filter((job) => ['inprogress', 'paused', 'onhold', 'pending', 'booked'].includes(job.status?.toLowerCase())).length;
  const urgentCount = jobs.filter((job) => ['high', 'urgent'].includes(job.priority?.toLowerCase())).length;
  const completedCount = jobs.filter((job) => job.status?.toLowerCase() === 'completed' && job.completedDate && new Date(job.completedDate).toDateString() === new Date().toDateString()).length;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
      <div className="mb-7 flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div><p className="text-xs font-bold uppercase tracking-[.18em] text-orange-600">Workshop floor</p><h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">My work queue</h1><p className="mt-2 text-sm text-slate-500">Your assigned jobs, live updates, and workshop conversations in one place.</p></div>
        <div className="relative w-full xl:w-80"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search RO, vehicle, plate…" className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm shadow-sm outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100" /></div>
      </div>
      <div className="mb-7 grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="flex items-center gap-2 text-slate-500"><Wrench className="h-4 w-4 text-blue-600" /><span className="text-xs font-semibold">Active</span></div><p className="mt-2 text-2xl font-bold">{activeCount}</p></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="flex items-center gap-2 text-slate-500"><AlertTriangle className="h-4 w-4 text-orange-500" /><span className="text-xs font-semibold">Priority</span></div><p className="mt-2 text-2xl font-bold">{urgentCount}</p></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="flex items-center gap-2 text-slate-500"><CheckCircle2 className="h-4 w-4 text-emerald-600" /><span className="text-xs font-semibold">Done today</span></div><p className="mt-2 text-2xl font-bold">{completedCount}</p></div>
      </div>
      {/* New Assignment Banner */}
      {newAssignmentBanner && (
        <div className="w-full mb-4 p-4 bg-surface-container-high border border-border-subtle rounded-lg shadow-sm flex items-center justify-between animate-slide-down">
          <div className="flex items-center gap-3 flex-1">
            <svg className="w-6 h-6 text-on-primary-fixed-variant" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
            </svg>
            <div>
              <p className="font-title-md text-title-md">New job assigned</p>
              <p className="font-body-sm text-body-sm text-secondary">{newAssignmentBanner.VehicleInfo}</p>
            </div>
          </div>
          <button
            onClick={() => {
              navigate(`/tech/job/${newAssignmentBanner.WorkOrderId}`);
              setNewAssignmentBanner(null);
            }}
            className="px-6 py-2 bg-primary-container text-on-primary-container font-label-md rounded-lg hover:bg-primary transition-colors whitespace-nowrap ml-4"
          >
            View
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 flex gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
        {['active', 'all', 'completed'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab as any)}
            className={`flex-1 whitespace-nowrap rounded-xl px-5 py-2.5 text-sm font-bold transition-colors ${
              activeTab === tab
                ? 'bg-[#14345a] text-white shadow-sm'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            {tab === 'active' ? 'Active' : tab === 'all' ? 'All' : 'Completed Today'}
          </button>
        ))}
      </div>

      {/* Jobs List */}
      {loadError ? <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">{loadError} <button onClick={loadJobs} className="ml-2 font-bold underline">Retry</button></div> : loading ? (
        <div className="text-center py-12">
          <p className="text-secondary">Loading jobs...</p>
        </div>
      ) : displayJobs.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-secondary">No jobs {activeTab === 'completed' ? 'completed today' : 'in this category'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
          {displayJobs.map(job => {
            const actionBtn = getActionButton(job);
            const statusColor = getStatusColor(job.status);
            const vehicleInfo = job.vehicleInfo || `${job.vehicleYear || ''} ${job.vehicleMake || ''} ${job.vehicleModel || ''}`.trim() || 'Vehicle details unavailable';

            return (
              <div
                key={job.workOrderId}
                className="group relative flex flex-col gap-5 overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg md:flex-row"
              >
                {/* Left color border */}
                <div
                  className="absolute top-0 left-0 w-2 h-full"
                  style={{ backgroundColor: statusColor }}
                />

                {/* Job Details */}
                <div className="flex-1 flex flex-col gap-3 pl-4">
                  {/* Header Row */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-label-md text-label-md" style={{
                      backgroundColor: `${statusColor}22`,
                      color: statusColor,
                      border: `1px solid ${statusColor}40`
                    }}>
                      {getStatusLabel(job.status)}
                    </span>
                    <span className="font-code-sm text-code-sm text-secondary bg-surface-container px-2 py-1 rounded">
                      RO# {job.workOrderNumber}
                    </span>
                  </div>

                  {/* Vehicle Name */}
                  <h2 className="font-headline-lg text-headline-lg text-on-surface">{vehicleInfo}</h2>

                  {/* Customer & Mileage */}
                  <div className="flex items-center gap-4 text-secondary font-body-md text-body-md">
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                      {job.customerName || `${job.customerFirstName || ''} ${job.customerLastName || ''}`.trim() || 'Customer'}
                    </span>
                    {job.vehiclePlate && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H6.5c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 7h11l1.96 5.5H4.54L6.5 7zm6 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" />
                        </svg>
                        Plate: {job.vehiclePlate}
                      </span>
                    )}
                  </div>

                  {/* Service Description */}
                  {(job.serviceDescription || job.description) && (
                    <p className="font-title-md text-title-md text-on-surface mt-2 bg-surface-light p-3 rounded-lg border border-border-subtle inline-block">
                      {job.serviceDescription || job.description}
                    </p>
                  )}
                </div>

                {/* Right side - Action */}
                <div className="md:w-64 flex flex-col justify-center gap-3 bg-surface-light p-4 rounded-lg border border-border-subtle md:border-0 md:bg-transparent md:p-0">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Clock3 className="h-4 w-4" />Added {new Date(job.createdDate).toLocaleDateString()}</div>
                  <button
                    onClick={() => navigate(`/tech/job/${job.workOrderId}`)}
                    className="w-full py-3 rounded-lg font-title-md text-title-md transition-colors"
                    style={{
                      backgroundColor: actionBtn.color,
                      color: '#ffffff'
                    }}
                  >
                    {actionBtn.label}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setChatJobId(job.workOrderId);
                      setUnreadChatJobs((prev) => {
                        const next = new Set(prev);
                        next.delete(job.workOrderId);
                        return next;
                      });
                    }}
                    className="w-full py-2 px-3 border-2 border-secondary text-secondary rounded-lg font-label-md hover:bg-secondary/10 transition-colors flex items-center justify-center gap-2 relative"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Message
                    {unreadChatJobs.has(job.workOrderId) && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        !
                      </span>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {chatJobId && (
        <ChatPanel
          workOrderId={chatJobId}
          vehicleInfo={jobs.find(j => j.workOrderId === chatJobId)?.vehicleInfo || 'Vehicle'}
          onClose={() => setChatJobId(null)}
        />
      )}
    </div>
  );
};
